import type { ReportColumn, ReportDefinition, ReportFilter } from './reports.gql';

// ── Conditional Format Parsing ────────────────────────────────────────────

export interface ConditionalFormatRule {
	condition: string;
	value: unknown;
	style: Record<string, string>;
}

/**
 * Parses the ConditionalFormatJson string from a report column into typed rules.
 * Expected JSON format: array of { condition, value, style } objects.
 * Example: [{ "condition": "lt", "value": 0, "style": { "color": "red" } }]
 */
export function parseConditionalFormat(json: string | null): ConditionalFormatRule[] {
	if (!json) return [];
	try {
		const parsed = JSON.parse(json);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(
			(r: unknown) =>
				r != null &&
				typeof r === 'object' &&
				'condition' in r &&
				'style' in r
		) as ConditionalFormatRule[];
	} catch {
		return [];
	}
}

// ── Dynamic Default Value Token Resolver ──────────────────────────────────

/**
 * Regex matching token strings: $anchor or $anchor±Nd/m/y
 * Examples: $today, $startOfMonth-1m, $today+7d, $startOfYear-1y
 */
const TOKEN_REGEX = /^\$(\w+)([+-]\d+[dmy])?$/;

/**
 * Formats a Date as YYYY-MM-DD for HTML date inputs.
 */
function formatDateForInput(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

/**
 * Resolves a named anchor keyword to a concrete Date relative to `now`.
 */
function resolveAnchor(anchor: string, now: Date): Date | null {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	switch (anchor) {
		case 'today':
			return today;
		case 'yesterday':
			return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
		case 'tomorrow':
			return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
		case 'startOfWeek': {
			const day = today.getDay();
			// Monday = start of week (ISO)
			const diff = day === 0 ? 6 : day - 1;
			return new Date(today.getFullYear(), today.getMonth(), today.getDate() - diff);
		}
		case 'endOfWeek': {
			const day = today.getDay();
			const diff = day === 0 ? 0 : 7 - day;
			return new Date(today.getFullYear(), today.getMonth(), today.getDate() + diff);
		}
		case 'startOfMonth':
			return new Date(today.getFullYear(), today.getMonth(), 1);
		case 'endOfMonth':
			return new Date(today.getFullYear(), today.getMonth() + 1, 0);
		case 'startOfQuarter': {
			const qMonth = Math.floor(today.getMonth() / 3) * 3;
			return new Date(today.getFullYear(), qMonth, 1);
		}
		case 'endOfQuarter': {
			const qMonth = Math.floor(today.getMonth() / 3) * 3 + 3;
			return new Date(today.getFullYear(), qMonth, 0);
		}
		case 'startOfYear':
			return new Date(today.getFullYear(), 0, 1);
		case 'endOfYear':
			return new Date(today.getFullYear(), 11, 31);
		default:
			return null;
	}
}

/**
 * Applies a signed offset like "+7d", "-1m", "-1y" to a date.
 */
function applyOffset(date: Date, offset: string): Date {
	const sign = offset[0] === '-' ? -1 : 1;
	const numStr = offset.slice(1, -1);
	const unit = offset[offset.length - 1];
	const amount = parseInt(numStr, 10) * sign;

	switch (unit) {
		case 'd':
			return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
		case 'm':
			return new Date(date.getFullYear(), date.getMonth() + amount, date.getDate());
		case 'y':
			return new Date(date.getFullYear() + amount, date.getMonth(), date.getDate());
		default:
			return date;
	}
}

/**
 * Resolves a filter's defaultValue. If the value starts with `$`, it is
 * interpreted as a dynamic token (e.g. `$today`, `$startOfMonth-1m`).
 * Otherwise the value is returned as-is (concrete literal).
 */
export function resolveDefaultValue(
	value: string,
	filterType: string
): string {
	if (!value.startsWith('$')) return value;

	const match = value.match(TOKEN_REGEX);
	if (!match) return value;

	const [, anchor, offset] = match;
	const now = new Date();

	let date = resolveAnchor(anchor, now);
	if (!date) return value; // Unknown anchor, pass through

	if (offset) {
		date = applyOffset(date, offset);
	}

	if (filterType === 'datetime') {
		return date.toISOString();
	}

	// Default: format as date string for HTML date inputs
	return formatDateForInput(date);
}

/**
 * Resolves all filter default values for a report definition.
 * Handles both dynamic tokens ($today, $startOfMonth-1m, etc.)
 * and concrete literal values.
 */
export function resolveFilterDefaults(
	filters: ReportFilter[]
): Record<string, unknown> {
	const defaults: Record<string, unknown> = {};
	for (const f of filters) {
		if (f.defaultValue) {
			defaults[f.key] = resolveDefaultValue(f.defaultValue, f.type);
		}
	}
	return defaults;
}

// ── Filter Validation ──────────────────────────────────────────────────────

export interface FilterValidationErrors {
	[filterKey: string]: string;
}

/**
 * Checks if a filter value is considered "empty" (missing, blank, undefined).
 */
function isFilterValueEmpty(value: unknown): boolean {
	if (value == null) return true;
	if (typeof value === 'string' && value.trim() === '') return true;
	return false;
}

/**
 * Validates that a date value is a valid date string.
 */
function isValidDate(value: unknown): boolean {
	if (typeof value !== 'string' || value.trim() === '') return false;
	const d = new Date(value);
	return !isNaN(d.getTime());
}

/**
 * Validates all filter values against their definitions.
 * Returns a map of filterKey → error message for any invalid filters.
 * An empty object means all filters are valid.
 */
export function validateFilters(
	filters: ReportFilter[],
	values: Record<string, unknown>
): FilterValidationErrors {
	const errors: FilterValidationErrors = {};

	for (const filter of filters) {
		const value = values[filter.key];

		// Required check
		if (filter.required && isFilterValueEmpty(value)) {
			errors[filter.key] = 'required';
			continue;
		}

		// Skip further validation if the value is empty and not required
		if (isFilterValueEmpty(value)) continue;

		// Type-specific validation
		if ((filter.type === 'date' || filter.type === 'datetime') && !isValidDate(value)) {
			errors[filter.key] = 'invalidDate';
		}
	}

	return errors;
}

/**
 * Resolves a dot-notation path (e.g. "vehicle.plateNumber") to a scalar value
 * inside a nested object. Returns undefined if the path doesn't exist.
 */
function resolvePath(obj: Record<string, unknown>, path: string): unknown {
	if (!path) return undefined;
	const parts = path.split('.');
	let current: unknown = obj;
	for (const part of parts) {
		if (current == null || typeof current !== 'object') return undefined;
		current = (current as Record<string, unknown>)[part];
	}
	return current;
}

/**
 * Given a GraphQL response JSON and a ReportDefinition, extracts the
 * result array and flattens each element into a flat key→value row.
 *
 * Uses `resultArrayPath` to locate the data array and each column's
 * `graphQlFieldPath` to extract the scalar value.
 */
export function flattenReportData(
	responseData: Record<string, unknown>,
	definition: ReportDefinition
): Record<string, unknown>[] {
	// Locate the data array using resultArrayPath
	const rawArray = resolvePath(responseData, definition.resultArrayPath);
	if (!Array.isArray(rawArray)) return [];

	return rawArray.map((item: unknown) => {
		const row: Record<string, unknown> = {};
		const record = item as Record<string, unknown>;
		for (const col of definition.columns) {
			const fieldPath = col.graphQlFieldPath || col.key;
			row[col.key] = resolvePath(record, fieldPath);
		}
		return row;
	});
}

/**
 * Formats a cell value based on the column's dataType and optional format token.
 */
export function formatCellValue(
	value: unknown,
	column: ReportColumn,
	locale = 'en'
): string {
	if (value == null) return '—';

	switch (column.dataType) {
		case 'date': {
			const d = new Date(String(value));
			if (isNaN(d.getTime())) return String(value);
			return d.toLocaleDateString(locale, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric'
			});
		}
		case 'datetime': {
			const d = new Date(String(value));
			if (isNaN(d.getTime())) return String(value);
			return d.toLocaleString(locale, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		}
		case 'currency': {
			const num = Number(value);
			if (isNaN(num)) return String(value);
			// column.format should be a valid ISO 4217 code (e.g. "EUR", "USD").
			// Guard against server returning non-code values like "currency".
			const raw = column.format;
			const isValidCurrencyCode = raw && /^[A-Z]{3}$/i.test(raw);
			const currencyCode = isValidCurrencyCode ? raw.toUpperCase() : 'EUR';
			try {
				return new Intl.NumberFormat(locale, {
					style: 'currency',
					currency: currencyCode
				}).format(num);
			} catch {
				// Fallback if the currency code is still somehow invalid
				return `€${num.toFixed(2)}`;
			}
		}
		case 'number':
		case 'integer': {
			const num = Number(value);
			if (isNaN(num)) return String(value);
			return new Intl.NumberFormat(locale).format(num);
		}
		case 'decimal': {
			const num = Number(value);
			if (isNaN(num)) return String(value);
			const decimals =
				column.format && !isNaN(Number(column.format))
					? Number(column.format)
					: 2;
			return new Intl.NumberFormat(locale, {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals
			}).format(num);
		}
		case 'boolean':
			return value ? 'Yes' : 'No';
		case 'duration': {
			const mins = Number(value);
			if (isNaN(mins)) return String(value);
			const hours = Math.floor(mins / 60);
			const remaining = Math.round(mins % 60);
			if (hours === 0) return `${remaining}m`;
			return `${hours}h ${remaining}m`;
		}
		default:
			return String(value);
	}
}

/**
 * Parses the default GraphQL variables JSON from the report definition
 * and merges with user-provided filter values.
 *
 * Date filter values (YYYY-MM-DD from HTML inputs) are converted to
 * ISO 8601 DateTime strings so the server's DateTime scalar can parse them.
 */
export function buildGraphQlVariables(
	definition: ReportDefinition,
	filterValues: Record<string, unknown>
): Record<string, unknown> {
	let defaults: Record<string, unknown> = {};
	if (definition.graphQlVariablesJson) {
		try {
			defaults = JSON.parse(definition.graphQlVariablesJson);
		} catch {
			// Invalid JSON, use empty defaults
		}
	}

	// Build a set of filter keys that are date-type for fast lookup
	const dateFilterKeys = new Set(
		definition.filters
			.filter(f => f.type === 'date' || f.type === 'datetime')
			.map(f => f.key)
	);

	// Convert date values to ISO 8601 DateTime strings
	const converted: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(filterValues)) {
		if (dateFilterKeys.has(key) && typeof value === 'string' && value) {
			// HTML date input gives "YYYY-MM-DD"; convert to ISO DateTime
			converted[key] = new Date(value).toISOString();
		} else {
			converted[key] = value;
		}
	}

	return { ...defaults, ...converted };
}

/**
 * Exports the report data to CSV format and triggers a download.
 */
export function exportToCsv(
	rows: Record<string, unknown>[],
	columns: ReportColumn[],
	filename: string,
	locale = 'en'
) {
	const visibleColumns = columns
		.filter(c => c.visibleByDefault)
		.sort((a, b) => a.sortOrder - b.sortOrder);

	const header = visibleColumns.map(c => `"${c.label}"`).join(',');
	const body = rows
		.map(row =>
			visibleColumns
				.map(col => {
					const val = formatCellValue(row[col.key], col, locale);
					return `"${val.replace(/"/g, '""')}"`;
				})
				.join(',')
		)
		.join('\n');

	const csv = `${header}\n${body}`;
	const blob = new Blob(['\ufeff' + csv], {
		type: 'text/csv;charset=utf-8;'
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${filename}.csv`;
	link.click();
	URL.revokeObjectURL(url);
}
